// Standard — "How we choose": the five rules every product must pass, live totals, the AI
// checker's status, and the latest automatic decisions, so visitors can see the curator at work.
import React, { useEffect, useState } from "react";
import { fill, useLang } from "./i18n";
import { WhyAdsLink } from "./KeepFree";
import { useMessages } from "./messages";

const API_BASE = "/api/bloom";
const OPEN_EVENT = "bloom:standard";

export function openStandard() {
    window.dispatchEvent(new Event(OPEN_EVENT));
}

type Decision = { asin: string; title: string; pass: boolean; failed: string[]; why: string; at: string; ledger?: string | null };
type Checker = { lastCheckAt: string | null; checkedToday: number; running: boolean; everyHours: number; rescanDays: number; pausedUntil?: string | null };
export type StandardData = {
    ok: boolean; rules: { minRatings: number }; checked: number; passed: number; listed: number;
    removed?: number; checker?: Checker; recent: Decision[];
};

function timeAgo(iso: string, lang: string) {
    const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    try {
        const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
        if (s < 3600) return rtf.format(-Math.max(1, Math.round(s / 60)), "minute");
        if (s < 86400) return rtf.format(-Math.round(s / 3600), "hour");
        return rtf.format(-Math.round(s / 86400), "day");
    } catch (e) {
        return new Date(iso).toLocaleString();
    }
}

// A UTC date and time in the visitor's language ("Oct 18, 05:45").
function utcTime(iso: string, lang: string) {
    try {
        return new Intl.DateTimeFormat(lang, { timeZone: "UTC", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
    } catch (e) {
        return new Date(iso).toISOString().slice(0, 16).replace("T", " ");
    }
}

// The AI checker, as it is: a pulsing dot while it is working, when it last looked at a product,
// how many it looked at today, and how often it runs (or until when it is paused after an Amazon robot check).
export function CheckerPulse({ data, full }: { data: StandardData | null; full?: boolean }) {
    const { lang } = useLang();
    const m = useMessages();
    const c = data && data.checker;
    if (!c || !c.lastCheckAt) return null;
    return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 shadow-sm ring-1 ring-emerald-100">
            <span className="relative flex h-2 w-2" aria-hidden="true">
                {c.running ? <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /> : null}
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            {c.running ? m.checkerNow : fill(m.checkerLast, { ago: timeAgo(c.lastCheckAt, lang) })}
            {" · "}{fill(m.checkerToday, { n: c.checkedToday.toLocaleString() })}
            {full ? " · " + (c.pausedUntil ? fill(m.checkerPaused, { t: utcTime(c.pausedUntil, lang) }) : fill(m.checkerEvery, { h: c.everyHours })) : ""}
        </span>
    );
}

export function useStandard(lang: string): StandardData | null {
    const [data, setData] = useState<StandardData | null>(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/standard?lang=${lang}`, { headers: { Accept: "application/json" } });
                const ctype = res.headers.get("content-type") || "";
                if (!res.ok || !ctype.includes("application/json")) return;
                const d = await res.json();
                if (!cancelled && d.ok) setData(d);
            } catch (e) {}
        })();
        return () => { cancelled = true; };
    }, [lang]);
    return data;
}

function Check({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
            <path d="m5 12 5 5L20 7" />
        </svg>
    );
}

export default function Standard({ data }: { data: StandardData | null }) {
    const { t } = useLang();
    const m = useMessages();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onOpen = () => setOpen(true);
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_EVENT, onOpen);
    }, []);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!open) return null;

    const rules = [
        { title: t.rule1T, body: fill(t.rule1, { n: (data?.rules.minRatings || 1000).toLocaleString() }) },
        { title: t.rule2T, body: t.rule2 },
        { title: t.rule3T, body: t.rule3 },
        { title: t.rule4T, body: t.rule4 },
        // Contract rule C: at most one thing to know; two or more never make the catalog.
        { title: t.rule5T, body: t.rule5 },
    ];

    return (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={t.stdTitle}>
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setOpen(false)} />
            <div
                className={
                    "absolute inset-x-0 bottom-0 flex max-h-[92vh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl " +
                    "animate-in max-md:slide-in-from-bottom duration-300 " +
                    "md:inset-auto md:left-1/2 md:top-1/2 md:max-h-[88vh] md:w-[600px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-3xl md:zoom-in-95"
                }
            >
                <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-200 md:hidden" />
                <div className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 px-6 pb-4 pt-5">
                    <button
                        onClick={() => setOpen(false)}
                        aria-label={t.close}
                        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl text-gray-500 shadow ring-1 ring-gray-100 transition hover:rotate-90 hover:text-gray-900"
                    >
                        &times;
                    </button>
                    <h2 className="pr-10 text-xl font-extrabold text-gray-900">{t.stdTitle}</h2>
                    <p className="mt-1 pr-6 text-sm text-gray-600">{t.stdIntro}</p>
                    {data ? (
                        <div className="mt-3 flex flex-wrap gap-2 text-xs font-bold text-green-800">
                            <span className="inline-flex rounded-full bg-white px-3 py-1 shadow-sm ring-1 ring-green-100">
                                {fill(t.stdChecked, { checked: data.checked.toLocaleString(), passed: data.passed.toLocaleString() })}
                            </span>
                            <CheckerPulse data={data} full />
                        </div>
                    ) : null}
                    {data && data.checker ? (
                        <p className="mt-2 text-xs leading-relaxed text-gray-600">{fill(m.rescanRule, { d: data.checker.rescanDays })}</p>
                    ) : null}
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-5">
                    <ol className="space-y-3">
                        {rules.map((r, i) => (
                            <li key={i} className="flex gap-3 rounded-2xl bg-white p-3 ring-1 ring-gray-100">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-600 text-sm font-extrabold text-white">{i + 1}</span>
                                <span>
                                    <span className="block text-sm font-bold text-gray-900">{r.title}</span>
                                    <span className="mt-0.5 block text-sm leading-relaxed text-gray-600">{r.body}</span>
                                    {i === 3 ? <span className="mt-1 block text-xs"><WhyAdsLink /></span> : null}
                                </span>
                            </li>
                        ))}
                    </ol>

                    {data && data.recent.length ? (
                        <div className="mt-5">
                            <p className="mb-2 text-xs font-bold uppercase tracking-widest text-gray-500">{t.stdRecent}</p>
                            <ul className="space-y-2">
                                {data.recent.map((d) => (
                                    <li key={d.asin + d.at} className="flex gap-2.5 rounded-xl bg-gray-50 p-2.5">
                                        <span className={"mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white " + (d.pass ? "bg-emerald-500" : "bg-gray-400")}>
                                            {d.pass ? <Check className="h-3 w-3" /> : <span className="text-xs font-bold leading-none">×</span>}
                                        </span>
                                        <span className="min-w-0">
                                            <span className="block truncate text-xs font-semibold text-gray-900">{d.title}</span>
                                            <span className="block text-[11px] text-gray-500">
                                                {d.ledger === "renewed" ? m.ledgerRenewed
                                                    : d.ledger === "strike" ? m.ledgerStrike
                                                        : d.ledger === "removed" ? m.ledgerRemoved
                                                            : d.pass ? t.stdPass : d.failed.map((f) => t.failReason[f] || f).join(" · ")}
                                                {d.why ? " — " + d.why : ""}
                                            </span>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    <p className="mt-4 text-[11px] leading-relaxed text-gray-400">{t.stdFootnote}</p>
                </div>

                <div className="border-t border-gray-100 bg-white p-4">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="block w-full rounded-xl bg-green-600 py-3 text-center text-sm font-bold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700"
                    >
                        {t.fundOk}
                    </button>
                </div>
            </div>
        </div>
    );
}
